import { useTranslation } from "@i18n";
import type { TranslatedString } from "@i18n";

import { useDispatch, useSelector } from "react-redux";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import { EstimateDsrItemsGridNew as EstimateDsrItems } from "@Features/estimation";
import { useRef, useState } from "react";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import IconComponent from "@CubeComponents/icons";
import { itemListSources } from "@Actions/estimation";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR } from "@Services/constants";
import { contractWBSController } from "@Features/contracts/state/contracts.actions";
import _ from "lodash";
import type { RootState } from "@Reducers/index";
import type {
  ContractWBSControllerRequest,
  ContractWBSControllerResponse,
} from "@ServerTypes/contracts/contracts.schema";

interface ChangeOrderState {
  isEnabled: boolean;
}

/**
 * An item produced by the `getAllFamilyOfEstimateItems` WBS controller action.
 * Matches a single entry of `ContractWBSControllerResponse["result"]`.
 */
type WbsEstimateFamilyItem = NonNullable<ContractWBSControllerResponse["result"]>[number];

/**
 * The `selectedItemsArray` element accepted by the `getAllFamilyOfEstimateItems`
 * WBS controller request. Used to type both the grid rows and the action payload.
 */
type SelectedItemPayload = Extract<
  ContractWBSControllerRequest,
  { action: "getAllFamilyOfEstimateItems" }
>["selectedItemsArray"][number];

/**
 * Shape of a row returned by the linked-BOQ grid's `dataView.getAllSelectedItems()`.
 * Only the fields read in this component are modelled; the grid is otherwise untyped.
 */
interface SelectedBoqRow {
  itemId: string;
  quantityLinked?: number;
  parentsList?: SelectedItemPayload["parentsList"];
  isAlreadyAddedInThisContract?: boolean;
  isAlreadyLinkedWithThisWBSItem?: boolean;
}

/**
 * Draft BOQ item row built from a `getAllFamilyOfEstimateItems` result entry and
 * handed off (untyped) to `addItemsCallBack`. Field types mirror the source
 * `WbsEstimateFamilyItem`; numeric quantity/amount fields can be `null` for parent
 * (group) rows. Mutated in a second pass to assign `serial` and change-order fields.
 */
interface LinkedBoqItemDraft {
  description: WbsEstimateFamilyItem["description"];
  HSN_SAC: WbsEstimateFamilyItem["HSN_SAC"];
  hasChild: WbsEstimateFamilyItem["hasChild"];
  itemLevel: WbsEstimateFamilyItem["itemLevel"];
  parentsList: WbsEstimateFamilyItem["parentsList"];
  type: string;
  code: WbsEstimateFamilyItem["code"];
  estimateId: WbsEstimateFamilyItem["estimateId"];
  estimateVersionId: WbsEstimateFamilyItem["estimateVersionId"];
  rateAnalysis: WbsEstimateFamilyItem["rateAnalysis"];
  itemId: WbsEstimateFamilyItem["itemId"];
  measurementUnit: WbsEstimateFamilyItem["measurementUnit"] | null;
  rate: WbsEstimateFamilyItem["rate"];
  purchaseUnit: WbsEstimateFamilyItem["purchaseUnit"] | null;
  parentId: WbsEstimateFamilyItem["parentId"];
  quantity: number | null;
  availableQuantityInParentEstimate: number;
  amount: number | null;
  availableQuantity: number | null;
  revisedQuantity: number | null;
  revisedAmount: number | null;
  changeOrderQuantity?: number | null;
  changeOrderAmount?: number | null;
  isAddedThroughChangeOrder?: boolean;
  serial?: number;
}

interface LinkBoqItemsProps {
  contractId?: string;
  hideBoqItemsModal: () => void;
  source: string;
  showBackBtn?: boolean;
  backBtnCallback?: () => void;
  addItemsCallBack: (data: unknown) => void;
  wbsOriginalItemId?: string;
  changeOrderState?: ChangeOrderState;
  showBtnLoader?: boolean;
}

function LinkBoqItems({
  contractId,
  hideBoqItemsModal,
  source,
  showBackBtn = false,
  backBtnCallback,
  addItemsCallBack,
  wbsOriginalItemId,
  changeOrderState,
  showBtnLoader = false,
}: LinkBoqItemsProps) {
  const { t } = useTranslation();
  const customQRef = useRef<any>({});
  const boqItemsRef = useRef<any>(null);
  const dispatch = useDispatch();

  const [linkBoqItemBtnState, setLinkBoqItemBtnState] = useState(false);
  const [autoPopulateState, setAutoPopulateState] = useState(false);

  const contractDetails = useSelector((state: RootState) => state.contractDetails);
  const currencyDetails = useSelector((state: RootState) => state.currencyDetails);

  const setBtnState = (state: boolean) => {
    setLinkBoqItemBtnState(state);
  };

  const handleLinkBoqItemsClick = async () => {
    const selectedBoqData: SelectedBoqRow[] = [
      ...boqItemsRef.current.dataView
        .getAllSelectedItems()
        .filter((record: SelectedBoqRow & { __isDummy?: boolean }) => record && !record.__isDummy),
    ];
    let isAlreadyAddedInThisContract = false;
    const isAlreadyLinkedWithThisWBSItem = false;
    let content: TranslatedString | undefined;
    selectedBoqData.map((data) => {
      if (data.isAlreadyAddedInThisContract) {
        isAlreadyAddedInThisContract = true;
        content = t("One of the selected Item(s) is already added in the contract");
      }
      if (data.isAlreadyLinkedWithThisWBSItem) {
        isAlreadyAddedInThisContract = true;
        content = t("One of the selected Item(s) is already linked with this wbs item");
      }
    });

    if ((isAlreadyAddedInThisContract || isAlreadyLinkedWithThisWBSItem) && content) {
      dispatch(
        showToastNotification({
          content,
          type: TOAST_TYPE_ERROR,
        }),
      );
    } else if (source == "contract") {
      const boqItems = selectedBoqData.map((data) => {
        if (data.isAlreadyAddedInThisContract) {
          isAlreadyAddedInThisContract = true;
        }
        const quantity = customQRef.current[data.itemId] ?? data.quantityLinked ?? 0;
        return {
          customQuantity: quantity,
          itemId: data.itemId,
        };
      });
      const data = {
        action: "link",
        entity: "boqItems",
        contractId,
        parentContractId: contractDetails.parentId,
        data: boqItems,
        autoPopulateAllAvailableQuantity: autoPopulateState,
      };
      addItemsCallBack(data);
    } else if (source == "WBSView" || source == "BoqView") {
      const selectedItemsArray = [...selectedBoqData].map((item) => ({
        itemId: item.itemId,
        parentsList: item.parentsList,
      }));

      const res = await contractWBSController({
        selectedItemsArray,
        action: "getAllFamilyOfEstimateItems",
        estimateVersionId: _.get(contractDetails, "linkedEstimateVersion.versionId"),
        estimateId: _.get(contractDetails, "linkedEstimateVersion.estimateId"),
        contractId: contractId!,
      });

      if (res.data.status) {
        const dataSource = (res.data.result ?? []).map((item) => {
          const totalQuantity =
            (item.manualQuantity ?? 0) + (item.quantityFromTakeoffs ?? 0) - (item.quantityUsedInThisContract ?? 0);
          const customQuantity = customQRef.current[item.itemId!] || 0;
          const quantityValue = item.hasChild ? null : customQuantity || (autoPopulateState ? totalQuantity : 0);
          const amountValue = item.hasChild
            ? null
            : customQuantity
              ? customQuantity * item.rate!
              : autoPopulateState
                ? totalQuantity * item.rate!
                : 0;
          const data: LinkedBoqItemDraft = {
            description: item.description,
            HSN_SAC: item.HSN_SAC,
            hasChild: item.hasChild,
            itemLevel: item.itemLevel,
            parentsList: item.parentsList,
            type: "ESTIMATE",
            code: item.code,
            estimateId: item.estimateId,
            estimateVersionId: item.estimateVersionId,
            rateAnalysis: item.rateAnalysis,
            itemId: item.itemId,
            measurementUnit: item.hasChild ? null : item.measurementUnit,
            rate: item.hasChild ? null : item.rate,
            purchaseUnit: item.hasChild ? null : item.purchaseUnit,
            parentId: item.parentId == "0" ? null : item.parentId,
            quantity: quantityValue,
            availableQuantityInParentEstimate:
              totalQuantity - (customQuantity || (autoPopulateState ? totalQuantity : 0)),
            amount: amountValue,
            availableQuantity: quantityValue,
            revisedQuantity: quantityValue,
            revisedAmount: amountValue,
          };
          if (changeOrderState!.isEnabled) {
            data.quantity = 0;
            data.amount = 0;
            data.changeOrderQuantity = quantityValue;
            data.changeOrderAmount = amountValue;
            data.isAddedThroughChangeOrder = true;
          } else {
            data.changeOrderQuantity = 0;
            data.changeOrderAmount = 0;
          }
          return data;
        });

        const childSerialMapper: Record<string, number> = {};
        const newDatasource = dataSource.map((item) => {
          if (item.parentId) {
            const parentSerial = childSerialMapper[item.parentId];
            if (parentSerial) {
              item.serial = parentSerial + 1;
              childSerialMapper[item.parentId] = parentSerial + 1;
            } else {
              item.serial = 1;
              childSerialMapper[item.parentId] = 1;
            }
          }
          return item;
        });
        addItemsCallBack(newDatasource);
      }
    }
  };

  const getCustomBoqQuantity = ({ itemId, value }: { itemId: string; value: number }) => {
    customQRef.current = { ...customQRef.current, [itemId]: value };
  };

  const boqModelContent = () =>
    currencyDetails.currencyDetail ? (
      <EstimateDsrItems
        contractId={contractId}
        contractVersionId={_.get(contractDetails, "versionId")}
        estimateVersionId={_.get(contractDetails, "linkedEstimateVersion.versionId")}
        estimateId={_.get(contractDetails, "linkedEstimateVersion.estimateId")}
        getCustomBoqQuantity={getCustomBoqQuantity}
        ref={boqItemsRef}
        setBtnState={setBtnState}
        enableCheckboxSelector
        source={itemListSources.LINK_BOQ_ITEMS}
        currencyData={{
          id: currencyDetails.currencyDetail?.id ?? "",
          selectedSymbol: { name: currencyDetails.symbolDetail?.name ?? "" },
        }}
        childParentMapper={{
          itemId: "itemId",
          parentId: "parentId",
        }}
        setAutoPopulateState={setAutoPopulateState}
        wbsOriginalItemId={wbsOriginalItemId}
      />
    ) : undefined;

  function BoqFooterContent() {
    return (
      <div className=" flex gap-4">
        <ButtonComponent
          disabled={!linkBoqItemBtnState}
          loading={showBtnLoader}
          buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
          onClick={handleLinkBoqItemsClick}
        >
          {["WBSView", "BoqView"].includes(source) ? t("Add") : t("Link")}
        </ButtonComponent>
        <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={hideBoqItemsModal}>
          {t("Cancel")}
        </ButtonComponent>
      </div>
    );
  }
  const boqHeaderContent = () => (
    <div className="  w-full flex justify-between rounded-md">
      <div className="flex items-center gap-2">
        {showBackBtn && (
          <IconComponent
            className="cursor-pointer"
            name="BackwardArrow"
            width="1rem"
            height="1rem"
            onClick={backBtnCallback}
          />
        )}
        {t("BOQ Items")}
      </div>
    </div>
  );

  return (
    <ModalStateDialogComponent
      allowModalPadding={false}
      footerContent={BoqFooterContent()}
      width="80%"
      height={"80%"}
      headerTitle={boqHeaderContent()}
      onCancel={hideBoqItemsModal}
      isVisible
      modelContent={boqModelContent()}
    />
  );
}
export default LinkBoqItems;
